import { NgClass } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, inject, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormControl, ValidationErrors, Validator } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import intlTelInput from 'intl-tel-input';
import * as intlTelInputUtils from 'intl-tel-input/build/js/utils.js';
import { distinctUntilChanged } from 'rxjs';
import { WIDGET_CONSTANTS } from '../../../../constants/widget-constants';
import { GeoService } from '../../../../services/geo.service';

@Component({
  selector: 'app-cw-intl-tel-input',
  templateUrl: './cw-intl-tel-input.component.html',
  styleUrl: './cw-intl-tel-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CwIntlTelInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CwIntlTelInputComponent),
      multi: true
    }
  ],
  imports: [FormsModule, NgClass, ReactiveFormsModule, FontAwesomeModule, TranslateModule]
})
export class CwIntlTelInputComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator, AfterViewInit {

  private geoService = inject(GeoService);

  @Input() defaultCountry: string;
  /** Lands on the inner input — on the host tag the browser never sees it. */
  @Input() autocomplete = '';
  @Input() cssClass: string;
  @Input() E164PhoneNumber: string;
  @Input() label: string;
  @Input() labelCssClass: string;
  @Input() name = 'intl-tel-input-name';
  @Input() onlyLocalized: boolean = true;
  @Input() options: any = {};
  @Input() required: boolean;
  @Input() showRequestSmsCodeButton = false;
  @Input() smsPhoneButtonDisabled = false;
  @Input() smsButtonloading = false;
  @Input() disabled: boolean;

  @Output() requestSmsCodeEvent = new EventEmitter<any>();
  @Output() blurEvent = new EventEmitter<any>();

  @ViewChild('intlTelInput') private _inputElement: ElementRef;

  telInputControl = new UntypedFormControl();

  private _intlTelInput: any;
  private shadowRootClickGuard: { root: ShadowRoot, listener: (event: Event) => void } | undefined;
  private onTouch: Function = () => void (0);
  private onModelChange: Function = () => void (0);
  private onValidatorChange: Function = () => void (0);

  ngOnInit(): void {
    this.telInputControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(
        (inputValue: string) => {
          if (!inputValue) {
            if (this.onModelChange) {
              this.onModelChange(inputValue);
            }
          }
          if (inputValue && this.onModelChange) {
            this.setPhoneNumber(inputValue);
            if (this.onTouch && !this.telInputControl.touched) {
              this.onTouch();
            }
            if (this.onValidatorChange) {
              this.onValidatorChange();
            }
          }
        }
      );
  }

  onClick(): void {
    if (this.onTouch) {
      this.onTouch();
    }
  }

  onBlur(): void {
    if (this.onTouch) {
      this.onTouch();
    }
    this.blurEvent.emit();
  }

  private static modifyCountryData(): void {
    intlTelInput.getCountryData().forEach((country: any) =>
      country.name = country.name.replace(/.+\((.+)\)/, '$1'));
  }

  public ngAfterViewInit(): void {
    if (this.onlyLocalized) {
      CwIntlTelInputComponent.modifyCountryData();
    }
    const fullscreenPopup = CwIntlTelInputComponent.useFullscreenPopup();
    this.options = {
      initialCountry: this.defaultCountry || 'auto',
      separateDialCode: true,
      preferredCountries: WIDGET_CONSTANTS.PREFFERED_COUNTRIES,
      formatOnDisplay: true,
      autoHideDialCode: true,
      countrySearch: true,
      loadUtils: () => Promise.resolve({ ...intlTelInputUtils }),
      // null = accept any valid phone number type (mobile, landline, VOIP, toll-free, etc.)
      // Restricting to specific types like ["MOBILE", "FIXED_LINE"] would reject numbers
      // classified as FIXED_LINE_OR_MOBILE (ambiguous) — common in US, Canada, and others.
      validationNumberTypes: null,
      geoIpLookup: callback => {
        this.geoService.lookup()
          .then(data => callback(data?.country || 'ch'))
          .catch(() => callback('ch'));
      },
      // Shadow DOM web component: keep the mobile fullscreen popup INSIDE the shadow tree. The library
      // appends it to `document.body` by default, where the host page's global CSS restyles it (the
      // country list is a plain `ul` / `li` / `a` structure) and the widget's own intl-tel-input styles
      // do not reach it. The widget root is a plain block (`all: initial`), so `position: fixed`
      // still positions the popup against the viewport. Only for the fullscreen popup: a container is
      // consumed when the markup is built and would turn the desktop dropdown into a JS-positioned
      // floating container too, so the library's own mobile heuristic is mirrored here and the
      // inline desktop dropdown stays exactly as it was.
      useFullscreenPopup: fullscreenPopup,
      dropdownContainer: fullscreenPopup ? this.shadowRootDropdownContainer() : null,
    };

    const intlTelInputInstance = intlTelInput;
    this._intlTelInput = intlTelInputInstance(this._inputElement.nativeElement, this.options);

    // Make iti non-enumerable to prevent GTM JSON.stringify circular reference error
    Object.defineProperty(this._inputElement.nativeElement, 'iti', {
      value: this._intlTelInput,
      enumerable: false,
      writable: true,
      configurable: true
    });

    this.installShadowRootClickGuard();
  }

  ngOnDestroy(): void {
    this.shadowRootClickGuard?.root.removeEventListener('click', this.shadowRootClickGuard.listener);
    this.shadowRootClickGuard = undefined;
  }

  /** intl-tel-input's own default for `useFullscreenPopup` (v25: mobile UA, or a narrow viewport, or coarse pointer + short viewport). */
  private static useFullscreenPopup(): boolean {
    if (typeof navigator === 'undefined' || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    const isMobileUserAgent = /Android.+Mobile|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const mq = (query: string) => window.matchMedia(query).matches;
    return isMobileUserAgent || mq('(max-width: 500px)') || (mq('(pointer: coarse)') && mq('(max-height: 600px)'));
  }

  /** The widget's root element when this input renders inside a shadow root, otherwise `null` (= library default, `document.body`). */
  private shadowRootDropdownContainer(): HTMLElement | null {
    const root = this._inputElement.nativeElement.getRootNode();
    if (typeof ShadowRoot === 'undefined' || !(root instanceof ShadowRoot)) {
      return null;
    }
    return root.querySelector<HTMLElement>('.calenso-booking');
  }

  /**
   * Shadow DOM web component only. intl-tel-input closes its open country dropdown from a `click`
   * listener on `document.documentElement` that inspects `event.target.closest(<dropdown>)`. Seen
   * from the document, every click that originates inside a shadow tree is re-targeted to the
   * shadow HOST, so a click on the dropdown's own search field (or scrollbar) looks like a click
   * outside and closes the list. Clicks that start inside the inline dropdown are therefore
   * stopped at the shadow root: they never reach the document-level handler, everything below
   * (the country list's own listeners, Angular's) has already run. Side effect, accepted: those
   * clicks are also invisible to the host page's own document-level listeners (analytics). The
   * mobile fullscreen popup lives inside the widget root as well (`dropdownContainer`), so the same
   * guard covers it; a click on its backdrop still propagates and closes it.
   */
  private installShadowRootClickGuard(): void {
    const root = this._inputElement.nativeElement.getRootNode();
    if (typeof ShadowRoot === 'undefined' || !(root instanceof ShadowRoot)) {
      return;
    }
    const listener = (event: Event) => {
      const target = event.target as Element | null;
      if (target?.closest?.('.iti__dropdown-content')) {
        event.stopPropagation();
      }
    };
    root.addEventListener('click', listener);
    this.shadowRootClickGuard = { root, listener };
  }

  setPhoneNumber(value: string) {
    if (this._intlTelInput) {
      if (!!value) {
        this._intlTelInput.setNumber(value);
      }
      // this._phoneNumber = value;
      this.i18nizePhoneNumber();
    }
  }

  public i18nizePhoneNumber(): void {
    this.E164PhoneNumber = this._intlTelInput.getNumber();
    if (this.E164PhoneNumber) {
      this.onModelChange(this.E164PhoneNumber);
    } else {
      if (this._intlTelInput.selectedDialCode && this._intlTelInput.selectedDialCode.innerText) {
        if (this.telInputControl.value && this.telInputControl.value[0] === '+') {
          this.E164PhoneNumber = this._intlTelInput.selectedDialCode.innerText + this._intlTelInput.telInput.value;
        } else {
          this.E164PhoneNumber = this._intlTelInput.selectedDialCode.innerText + this.telInputControl.value;
        }
        this.onModelChange(this.E164PhoneNumber);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.telInputControl.disable();
    } else {
      this.telInputControl.enable();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.setDisabledState(changes['disabled'].currentValue);
    }
  }

  writeValue(obj: any): void {
    this.telInputControl.setValue(obj);
  }

  validate(control: AbstractControl): ValidationErrors {
    if (control.value && this._intlTelInput) {
      const selectedCountryCode = this._intlTelInput.getSelectedCountryData();
      if (control.value === `+${selectedCountryCode.dialCode}`) {
        if (this.required) {
          control.setErrors({
            required: true
          });
          return control.errors;
        }
      } else if (!this._intlTelInput.isValidNumber() && this._intlTelInput.isValidNumber() !== null) {
        control.setErrors({
          invalidNumber: true
        });
        return control.errors;
      }
    }
    return null;
  }

  registerOnValidatorChange(fn: any): void {
    this.onValidatorChange = fn;
  }

  onReqestSmsCode(): void {
    if (this.validate(this.telInputControl) === null) {
      this.requestSmsCodeEvent.emit();
    }
  }
}
